import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GiftSuccessPage = () => {
  const [status, setStatus] = useState('checking'); // 'checking', 'success', 'error'
  const [message, setMessage] = useState('Processing your payment...');
  const [giftCodes, setGiftCodes] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        // Get session_id from URL
        const params = new URLSearchParams(location.search);
        const sessionId = params.get('session_id');

        if (!sessionId) {
          setStatus('error');
          setMessage('No payment session found');
          return;
        }

        // Check payment status and activate gift codes
        const response = await axios.get(`${API}/gift-subscriptions/status/${sessionId}`);

        if (response.data.status === 'paid') {
          setStatus('success');
          setMessage('Your gift subscriptions have been sent!');
          
          // Fetch gift codes to display
          if (response.data.gift_codes) {
            setGiftCodes(response.data.gift_codes);
          }
        } else {
          setStatus('checking');
          setMessage('Payment is still processing. Please check back in a moment.');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('error');
        setMessage('Error verifying payment. Please contact support if you were charged.');
      }
    };

    checkPaymentStatus();
  }, [location]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-white to-cream flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-cream border-burgundy/20">
        <CardHeader>
          {status === 'checking' && (
            <div className="flex mb-4">
              <Loader2 className="h-12 w-12 text-burgundy animate-spin" />
            </div>
          )}
          {status === 'success' && (
            <div className="flex mb-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
          )}
          {status === 'error' && (
            <div className="flex mb-4">
              <XCircle className="h-12 w-12 text-red-600" />
            </div>
          )}
          <CardTitle className="text-2xl font-serif text-burgundy text-left">
            {status === 'success' && 'Gift Sent Successfully!'}
            {status === 'checking' && 'Processing Payment...'}
            {status === 'error' && 'Payment Status Unknown'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-left text-burgundy/80">{message}</p>
          
          {status === 'success' && giftCodes.length > 0 && (
            <div className="bg-burgundy/5 rounded-lg p-4 border border-burgundy/20">
              <p className="font-semibold text-burgundy mb-3">Your Gift Codes</p>
              <p className="text-xs text-burgundy/70 mb-3">Share these codes with your recipients. They can redeem them by clicking their name → Gift Subscription</p>
              <div className="space-y-2">
                {giftCodes.map((code, index) => (
                  <div key={index} className="bg-white rounded p-3 border border-burgundy/20 flex justify-between items-center">
                    <div>
                      <p className="text-xs text-burgundy/60">Recipient #{index + 1}</p>
                      <p className="font-mono text-sm font-bold text-burgundy">{code.code}</p>
                      <p className="text-xs text-burgundy/60">{code.email}</p>
                    </div>
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(code.code);
                        alert('Code copied to clipboard!');
                      }}
                      size="sm"
                      variant="outline"
                      className="border-burgundy text-burgundy hover:bg-burgundy hover:text-white text-xs"
                    >
                      Copy
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {status === 'success' && (
            <div className="bg-gold/10 rounded-lg p-4 border border-gold/30">
              <p className="text-sm text-burgundy">
                <strong>What happens next:</strong>
              </p>
              <ul className="text-sm text-burgundy/80 mt-2 space-y-1 list-disc list-inside">
                <li>Recipients will receive an email with their unique gift code</li>
                <li>You'll receive a confirmation email shortly</li>
                <li>Gift codes are valid for 90 days</li>
                <li>If emails don't arrive, you can share the codes above directly</li>
              </ul>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Button
              onClick={() => navigate('/')}
              className="w-full bg-burgundy hover:bg-burgundy/80 text-white"
            >
              Return to Home
            </Button>
            {status === 'error' && (
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="w-full border-burgundy text-burgundy hover:bg-burgundy hover:text-white"
              >
                Try Again
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GiftSuccessPage;
